import React from 'react';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import { Formik } from 'formik';
import { Field, FieldProps } from 'formik';
import { Form } from 'formik';
import { Mutation } from '@apollo/client/react/components';

import SVGIconView from 'lib/images/svg-icon-view';
import ImageView from 'lib/images/image-view';

import { AlertContext } from 'lib/alert-context/alert-context';
import addToGroup from 'four-nets/icons/svg/icon_add-to-group.svg';
import iconDelete from 'four-nets/icons/svg/icon_delete.svg';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import ImageUpload from 'four-nets/ui-v2/form/image-upload/image-upload';
import { PhotoFileFragment } from 'four-nets/photofile/photofile.graphql';

import { InfoSettingsFragment } from './info-settings.graphql';
import mutation, {
    UpdateGroupInfoMutation,
    UpdateGroupInfoMutationVariables,
} from './mutations/update-group-info.graphql';
import deleteMutation, {
    DeleteGroupCoverPhotoMutation,
    DeleteGroupCoverPhotoMutationVariables,
} from './mutations/delete-group-cover-photo.graphql';
import defaultStyles from './info-settings.scss';

export interface Props extends InfoSettingsFragment {
    styles: typeof defaultStyles;
}

export interface State {
    progress: number;
    image?: PhotoFileFragment;
    key: number;
}

interface Values {
    description: string;
    rules: string;
}

type Errors = Partial<{ [key in keyof Values]: Array<React.ReactElement<FormattedMessage>> | true }>;

class GroupInfoSettings extends React.PureComponent<Props, State> {
    state: State = { progress: 0, key: 0 };

    onChange = (image: PhotoFileFragment | undefined) => {
        this.setState({ image });
    };

    render() {
        const { styles, id, coverPhoto } = this.props;
        const { image } = this.state;

        return (
            <Mutation<UpdateGroupInfoMutation, UpdateGroupInfoMutationVariables> mutation={mutation}>
                {updateGroupInfo => (
                    <AlertContext.Consumer>
                        {({ retry }) => (
                            <Formik<Values>
                                validate={values => {
                                    const errors: Errors = {};
                                    const descriptionErrors = mergeValidators([emptyValidator])(values.description);
                                    if (descriptionErrors) {
                                        errors.description = descriptionErrors;
                                    }
                                    return errors;
                                }}
                                initialValues={{
                                    description: this.props.description || '',
                                    rules: this.props.rules || '',
                                }}
                                onSubmit={(values, actions) => {
                                    actions.setSubmitting(true);
                                    const onAccept = () => {
                                        updateGroupInfo({
                                            variables: {
                                                input: {
                                                    id,
                                                    ...values,
                                                    photoFileId: image && image.id,
                                                },
                                            },
                                        })
                                            .then(() => actions.setTouched({ description: false, rules: false }))
                                            .catch((err: Error) => retry({ onAccept, body: err.message }))
                                            .finally(() => {
                                                this.setState(prevState => ({
                                                    key: prevState.key + 1,
                                                    image: undefined,
                                                }));
                                                actions.setSubmitting(false);
                                            });
                                    };
                                    onAccept();
                                }}
                            >
                                {formik => {
                                    const {
                                        isSubmitting,
                                        isValid,
                                        submitForm,
                                        setFieldValue,
                                        touched,
                                        errors,
                                    } = formik;
                                    return (
                                        <Form className={styles.form}>
                                            <FormattedMessage id='GROUP_COVER_PHOTO'>
                                                {msg => <div className={styles.label}>{msg}</div>}
                                            </FormattedMessage>
                                            {coverPhoto && coverPhoto.src ? (
                                                <React.Fragment>
                                                    <div className={styles.coverImage}>
                                                        <ImageView
                                                            sizes={{ xs: 120, lg: 1280 }}
                                                            src={coverPhoto && coverPhoto.src}
                                                            srcSet={coverPhoto && coverPhoto.srcset!}
                                                            aspectFill={true}
                                                        />
                                                    </div>
                                                    <Mutation<
                                                        DeleteGroupCoverPhotoMutation,
                                                        DeleteGroupCoverPhotoMutationVariables
                                                    >
                                                        mutation={deleteMutation}
                                                    >
                                                        {deleteGroupPhoto => (
                                                            <AlertContext.Consumer>
                                                                {({ confirm }) => (
                                                                    <button
                                                                        type='button'
                                                                        className={styles.deleteButton}
                                                                        onClick={() => {
                                                                            const onAccept = () => {
                                                                                deleteGroupPhoto({
                                                                                    variables: { input: { id } },
                                                                                }).then(() =>
                                                                                    this.setState(prevState => ({
                                                                                        key: prevState.key + 1,
                                                                                        image: undefined,
                                                                                    }))
                                                                                );
                                                                            };

                                                                            confirm({
                                                                                acceptButtonLabel: (
                                                                                    <FormattedMessage id='DELETE_COVER_PHOTO' />
                                                                                ),
                                                                                body: (
                                                                                    <FormattedMessage id='ARE_YOU_SURE_TO_DELETE_THIS_COVER_PHOTO' />
                                                                                ),
                                                                                onAccept,
                                                                            });
                                                                        }}
                                                                    >
                                                                        <SVGIconView src={iconDelete} />
                                                                        <FormattedMessage id='DELETE_COVER_PHOTO' />
                                                                    </button>
                                                                )}
                                                            </AlertContext.Consumer>
                                                        )}
                                                    </Mutation>
                                                </React.Fragment>
                                            ) : (
                                                <label>
                                                    <div className={styles.imageUpload}>
                                                        <ImageUpload onChange={this.onChange} key={this.state.key}>
                                                            <FormattedMessage id='ADD_PHOTO' />
                                                        </ImageUpload>
                                                    </div>
                                                    <FormattedMessage id='GROUP_COVER_SHOULD_BE_LANDSCAPE._WIDTH_MORE_THAN_750_PIXELS.'>
                                                        {msg => <span className={styles.description}>{msg}</span>}
                                                    </FormattedMessage>
                                                </label>
                                            )}
                                            <Field name='description'>
                                                {({ field }: FieldProps) => (
                                                    <React.Fragment>
                                                        <label>
                                                            <FormattedMessage id='GROUP_DESCRIPTION'>
                                                                {msg => <div className={styles.label}>{msg}</div>}
                                                            </FormattedMessage>
                                                            <TextArea
                                                                errors={
                                                                    errors.description
                                                                        ? [errors.description]
                                                                        : undefined
                                                                }
                                                                touched={touched.description}
                                                                {...field}
                                                                // FIXME: 'field.onChange' handler doesn't work with TextArea
                                                                onChange={e =>
                                                                    setFieldValue('description', e.target.value)
                                                                }
                                                            />
                                                        </label>
                                                        <FormattedMessage id='GROUPS_ENTER_WHAT_IS_GROUP_ABOUT_AND_WHO_ARE_THEIR_MEMBERS.'>
                                                            {msg => <span className={styles.description}>{msg}</span>}
                                                        </FormattedMessage>
                                                    </React.Fragment>
                                                )}
                                            </Field>
                                            <Field name='rules'>
                                                {({ field }: FieldProps) => (
                                                    <React.Fragment>
                                                        <label>
                                                            <FormattedMessage id='GROUP_RULES'>
                                                                {msg => <div className={styles.label}>{msg}</div>}
                                                            </FormattedMessage>
                                                            <TextArea
                                                                errors={errors.rules ? [errors.rules] : undefined}
                                                                touched={touched.rules}
                                                                {...field}
                                                                // FIXME: 'field.onChange' handler doesn't work with TextArea
                                                                onChange={e => setFieldValue('rules', e.target.value)}
                                                            />
                                                        </label>
                                                    </React.Fragment>
                                                )}
                                            </Field>
                                            <button
                                                type='button'
                                                className={styles.submitButton}
                                                onClick={submitForm}
                                                disabled={
                                                    !image &&
                                                    (!isValid ||
                                                        isSubmitting ||
                                                        (!touched.description && !touched.rules))
                                                }
                                            >
                                                <SVGIconView src={addToGroup} />
                                                <FormattedMessage id='BUTTON_LABEL_SAVE' />
                                            </button>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        )}
                    </AlertContext.Consumer>
                )}
            </Mutation>
        );
    }
}

export default Styled(defaultStyles)(GroupInfoSettings);
